import { PeakDetectionMarkerNormalizationSummary } from 'api/slideMultiplexChannelNormalizations';
import { MultiplexPanelView } from 'interfaces/multiplexPanels';
import {
  compact,
  filter,
  find,
  first,
  flatMap,
  groupBy,
  includes,
  intersection,
  isFinite,
  join,
  keys,
  map,
  max,
  min,
  reduce,
  size,
  uniq,
} from 'lodash';
import { formatSlideResolution } from 'utils/helpers';
import { CasesParams } from 'utils/useCasesParams/types';
import { NormalizationCoverageStatus } from '../helpers';

import {
  MultiplexPeakDetectionChannelProfile,
  MultiplexPeakDetectionFormValues,
  MultiplexPeakDetectionProfile,
  MultiplexRawDogPeakDetectionChannelProfile,
  PanelPeakDetectionConfiguration,
  multiplexPeakDetectionDefaultMinDistancePx,
  multiplexPeakDetectionDefaultThreshold,
  multiplexRawDogPeakDetectionDefaults,
} from './types';

export interface PeakDetectionSpacingDistanceGroup {
  umpp: number;
  distanceUm: number;
  slideCount: number;
  isCustom: boolean;
}

export const buildDefaultPeakDetectionChannelProfile = (): MultiplexPeakDetectionChannelProfile => ({
  threshold: multiplexPeakDetectionDefaultThreshold,
  minDistancePx: multiplexPeakDetectionDefaultMinDistancePx,
  minRadiusUm: null,
  maxRadiusUm: null,
});

export const buildDefaultRawDogPeakDetectionChannelProfile = () => ({ ...multiplexRawDogPeakDetectionDefaults });

export const buildPanelPeakDetectionRequests = ({
  panels,
  panelConfigurations,
  selectedSlideIds,
  casesParams,
  jobName,
  jobDescription,
  normalizationName,
  resolutionUmpp,
}: {
  panels: MultiplexPanelView[];
  panelConfigurations: Record<string, PanelPeakDetectionConfiguration>;
  selectedSlideIds: string[];
  casesParams: CasesParams;
  jobName: string;
  jobDescription: string;
  normalizationName?: string | null;
  resolutionUmpp?: number | null;
}): Array<{ panelId: string; request: CasesParams & MultiplexPeakDetectionFormValues }> => {
  if (!size(selectedSlideIds)) throw new Error('Select slides before running spot detection');
  const filterStudyId = typeof casesParams.filters?.studyId === 'string' ? casesParams.filters.studyId : undefined;
  const studyId = casesParams.studyId || filterStudyId;
  if (!studyId || (filterStudyId && filterStudyId !== studyId)) {
    throw new Error('Select one consistent study before running spot detection');
  }
  const requests = map(keys(panelConfigurations), (panelId) => {
    const panel = find(panels, { panelId });
    if (!panel) throw new Error('Unknown panel; refresh panel information');
    const configuration = panelConfigurations[panelId];
    const slideIds = intersection(map(panel.slides, 'slideId'), selectedSlideIds);
    if (!size(slideIds)) throw new Error('The panel has no selected member slides');
    if (!size(configuration.channelNames)) throw new Error('Select at least one marker for each panel');
    const overriddenChannels = flatMap(configuration.overrides, 'channelNames');
    if (size(uniq(overriddenChannels)) !== size(overriddenChannels)) {
      throw new Error('Override marker sets must not overlap');
    }
    for (const override of configuration.overrides) {
      if (!size(override.channelNames)) throw new Error('Select markers for each override');
      for (const channelName of override.channelNames) {
        if (!includes(configuration.channelNames, channelName))
          throw new Error('Override marker is outside the selection');
      }
    }
    const profiles = reduce(
      configuration.channelNames,
      (result, channelName) => {
        const channel = find(panel.channels, (entry) => entry.saved?.analysisName === channelName);
        if (!channel?.saved || channel.saved.channelModality === 'dna') {
          throw new Error('Unknown or DNA marker in spot detection selection');
        }
        for (const slide of filter(panel.slides, (entry) => includes(slideIds, entry.slideId))) {
          const channelIndex = find(
            keys(slide.channelMapping),
            (index) => slide.channelMapping[index] === channel.channelIndex
          );
          if (channelIndex == null || slide.channelMarkerTypes[channelIndex] !== channelName) {
            throw new Error('A selected slide has a different assigned marker; refresh panel information');
          }
        }
        const override = find(configuration.overrides, (entry) => includes(entry.channelNames, channelName));
        const profile = override?.profile || configuration.defaultProfile;
        if (!isFinite(profile.minDistancePx) || profile.minDistancePx < 1 || !Number.isInteger(profile.minDistancePx)) {
          throw new Error('Minimum spot spacing must be a positive integer');
        }
        const threshold = configuration.useRawDog
          ? (profile as MultiplexRawDogPeakDetectionChannelProfile).clipMinFraction
          : (profile as MultiplexPeakDetectionChannelProfile).threshold;
        if (!isFinite(threshold) || threshold < 0 || threshold > 1)
          throw new Error('Signal fraction must be between 0 and 1');
        if (
          configuration.useRawDog &&
          threshold >= (profile as MultiplexRawDogPeakDetectionChannelProfile).clipMaxFraction
        ) {
          throw new Error('Lower clipping fraction must be below the upper fraction');
        }
        if (profile.minRadiusUm != null && (!isFinite(profile.minRadiusUm) || profile.minRadiusUm < 0))
          throw new Error('Minimum radius must be nonnegative');
        if (profile.maxRadiusUm != null && (!isFinite(profile.maxRadiusUm) || profile.maxRadiusUm < 0))
          throw new Error('Maximum radius must be nonnegative');
        if (profile.minRadiusUm != null && profile.maxRadiusUm != null && profile.maxRadiusUm < profile.minRadiusUm)
          throw new Error('Maximum radius must not be below minimum radius');
        result[channelName] = profile;
        return result;
      },
      {} as Record<string, MultiplexPeakDetectionProfile>
    );
    return {
      panelId,
      request: {
        ...casesParams,
        studyId,
        slideIdsToInclude: slideIds,
        slidesMode: true,
        jobName,
        jobDescription,
        configParams: {
          channelNames: [...configuration.channelNames],
          profiles: sanitizePeakDetectionProfilesForSubmit(profiles, configuration.useRawDog),
          useRawDog: configuration.useRawDog,
          ...(!configuration.useRawDog && normalizationName ? { normalizationName } : {}),
          ...(resolutionUmpp != null && resolutionUmpp > 0 ? { resolutionUmpp } : {}),
        },
      },
    };
  });
  if (!size(requests)) throw new Error('Select a panel before running spot detection');
  const allSlideIds = flatMap(requests, (entry) => entry.request.slideIdsToInclude || []);
  if (size(uniq(allSlideIds)) !== size(allSlideIds))
    throw new Error('A slide cannot be selected in more than one panel');
  return requests;
};

export const resetPeakDetectionProfiles = ({
  selectedChannelNames,
  useRawDog,
}: {
  selectedChannelNames: string[];
  useRawDog: boolean;
}) =>
  reduce(
    selectedChannelNames,
    (accumulator, channelName) => {
      accumulator[channelName] = useRawDog
        ? buildDefaultRawDogPeakDetectionChannelProfile()
        : buildDefaultPeakDetectionChannelProfile();
      return accumulator;
    },
    {} as Record<string, MultiplexPeakDetectionProfile>
  );

export const synchronizePeakDetectionProfiles = ({
  selectedChannelNames,
  profiles,
  useRawDog = false,
}: {
  selectedChannelNames: string[];
  profiles?: Record<string, MultiplexPeakDetectionProfile> | null;
  useRawDog?: boolean;
}) => {
  return reduce(
    selectedChannelNames,
    (accumulator, channelName) => {
      accumulator[channelName] = {
        ...(useRawDog ? buildDefaultRawDogPeakDetectionChannelProfile() : buildDefaultPeakDetectionChannelProfile()),
        ...(profiles?.[channelName] || {}),
      };
      return accumulator;
    },
    {} as Record<string, MultiplexPeakDetectionProfile>
  );
};

export const sanitizePeakDetectionProfilesForSubmit = (
  profiles: Record<string, MultiplexPeakDetectionProfile>,
  useRawDog = false
) => {
  return reduce(
    profiles,
    (accumulator, profile, channelName) => {
      accumulator[channelName] = useRawDog
        ? profile
        : {
            threshold: (profile as MultiplexPeakDetectionChannelProfile).threshold,
            minDistancePx: profile.minDistancePx,
            ...(profile.minRadiusUm != null ? { minRadiusUm: profile.minRadiusUm } : {}),
            ...(profile.maxRadiusUm != null ? { maxRadiusUm: profile.maxRadiusUm } : {}),
          };
      return accumulator;
    },
    {} as Record<string, MultiplexPeakDetectionProfile>
  );
};

export const buildPeakDetectionNormalizationCoverageStatuses = ({
  normalizationSummaries,
  selectedChannelNames,
  channelLabelsByName,
  selectedSlideIds,
}: {
  normalizationSummaries?: PeakDetectionMarkerNormalizationSummary[];
  selectedChannelNames: string[];
  channelLabelsByName?: Record<string, string>;
  selectedSlideIds?: string[];
}) => {
  const summariesByMarkerType = reduce(
    normalizationSummaries || [],
    (accumulator, normalizationSummary) => {
      accumulator[normalizationSummary.markerType] = normalizationSummary;
      return accumulator;
    },
    {} as Record<string, PeakDetectionMarkerNormalizationSummary>
  );

  return map(selectedChannelNames, (channelName) => {
    const normalizationSummary = summariesByMarkerType[channelName];
    const normalizedSlideIds = uniq(flatMap(normalizationSummary?.rangeGroups || [], 'slideIds'));
    const missingSlideIds = normalizationSummary?.missingSlideIds || selectedSlideIds || [];
    const coverageSlideIds = uniq(selectedSlideIds || [...normalizedSlideIds, ...missingSlideIds]);

    return {
      channelName,
      channelLabel: channelLabelsByName?.[channelName] || channelName,
      totalSelectedSlidesCount: size(coverageSlideIds),
      normalizedSlideIds,
      missingSlideIds,
      isComplete: Boolean(normalizationSummary) && !size(missingSlideIds) && size(coverageSlideIds) > 0,
    } as NormalizationCoverageStatus;
  });
};

const formatRawPixelValue = (value: number) => {
  const roundedValue = Number(value.toFixed(4));
  return String(roundedValue);
};

export const formatPeakDetectionNormalizedThreshold = (threshold: number) => {
  if (threshold >= 0.1) {
    return String(Number(threshold.toFixed(2)));
  }

  if (threshold >= 0.01) {
    return String(Number(threshold.toFixed(3)));
  }

  return String(Number(threshold.toFixed(4)));
};

export interface PeakDetectionEffectiveThresholdGroup {
  minValue: number;
  maxValue: number;
  rangeLabel: string;
  effectiveThreshold: string;
  slideCount: number;
}

export const buildPeakDetectionEffectiveThresholdGroups = ({
  normalizationSummary,
  threshold,
}: {
  normalizationSummary?: PeakDetectionMarkerNormalizationSummary;
  threshold?: number;
}): PeakDetectionEffectiveThresholdGroup[] => {
  if (!normalizationSummary || !isFinite(threshold)) {
    return [];
  }

  return map(normalizationSummary.rangeGroups, ({ normalizationRange, slideIds }) => {
    const [minValue, maxValue] = normalizationRange;
    return {
      minValue,
      maxValue,
      rangeLabel: `${minValue}–${maxValue}`,
      effectiveThreshold: formatRawPixelValue(minValue + Number(threshold) * (maxValue - minValue)),
      slideCount: size(slideIds),
    };
  });
};

export const formatPeakDetectionEffectiveThresholdExplanation = ({
  threshold,
  thresholdGroup,
}: {
  threshold: number;
  thresholdGroup: PeakDetectionEffectiveThresholdGroup;
}) => {
  const slideCountLabel = thresholdGroup.slideCount === 1 ? '1 slide' : `${thresholdGroup.slideCount} slides`;

  return `${formatPeakDetectionNormalizedThreshold(threshold)} of ${thresholdGroup.rangeLabel} = ${
    thresholdGroup.effectiveThreshold
  } raw pixel intensity (${slideCountLabel})`;
};

export const formatPeakDetectionEffectiveThresholdVisibleLabel = (
  thresholdGroups?: PeakDetectionEffectiveThresholdGroup[]
) => {
  if (!size(thresholdGroups)) {
    return '';
  }

  const uniqueLabels = uniq(map(thresholdGroups, 'effectiveThreshold'));
  if (size(uniqueLabels) === 1) {
    return `${first(uniqueLabels)} raw pixel intensity`;
  }

  return `${join(uniqueLabels, ' / ')} raw pixel intensity`;
};

export const buildPeakDetectionNormalizationWarning = ({
  coverageStatuses,
  normalizationName,
}: {
  coverageStatuses: NormalizationCoverageStatus[];
  normalizationName?: string | null;
}) => {
  const totalSlidesCount = coverageStatuses[0]?.totalSelectedSlidesCount || 0;
  if (!totalSlidesCount) {
    return '';
  }

  const missingCoverageStatuses = filter(coverageStatuses, (coverageStatus) => !coverageStatus.isComplete);
  if (!size(missingCoverageStatuses)) {
    return '';
  }

  const missingChannelWarnings = compact(
    map(
      missingCoverageStatuses,
      (coverageStatus) =>
        `${coverageStatus.channelLabel} (${size(coverageStatus.missingSlideIds)}/${
          coverageStatus.totalSelectedSlidesCount
        } selected slides)`
    )
  );
  const namedResultWarning = normalizationName
    ? ` The named normalization result "${normalizationName}" has incomplete coverage.`
    : '';

  return `Selected channels are missing normalization coverage for ${join(
    missingChannelWarnings,
    ', '
  )}.${namedResultWarning}`;
};

export const getPeakDetectionUmppFromSlideMetadata = (slide?: { maxResolution?: number | null }) => {
  const umpp = slide?.maxResolution;
  return isFinite(umpp) && Number(umpp) > 0 ? Number(umpp) : null;
};

export const convertPeakDetectionDistancePxToUm = (minDistancePx?: number, umpp?: number | null) => {
  if (!isFinite(minDistancePx) || Number(minDistancePx) < 0 || !isFinite(umpp) || Number(umpp) <= 0) {
    return null;
  }

  return Number((Number(minDistancePx) * Number(umpp)).toFixed(4));
};

export const formatPeakDetectionDistanceUm = (distanceUm: number) => {
  if (distanceUm >= 10) {
    return `${Number(distanceUm.toFixed(1))} µm`;
  }

  if (distanceUm >= 1) {
    return `${Number(distanceUm.toFixed(2))} µm`;
  }

  return `${Number(distanceUm.toFixed(3))} µm`;
};

export const formatPeakDetectionSpacingDistanceLabel = (distanceGroup: PeakDetectionSpacingDistanceGroup) => {
  const resolutionLabel = formatSlideResolution(distanceGroup.umpp);
  const sourceLabel = distanceGroup.isCustom ? 'custom resolution' : resolutionLabel;

  return `${formatPeakDetectionDistanceUm(distanceGroup.distanceUm)} at ${sourceLabel}`;
};

export const formatPeakDetectionSpacingVisibleUmLabel = (distanceGroups?: PeakDetectionSpacingDistanceGroup[]) => {
  if (!size(distanceGroups)) {
    return '';
  }

  const distancesUm = map(distanceGroups, 'distanceUm');
  const minimumDistanceUm = min(distancesUm);
  const maximumDistanceUm = max(distancesUm);

  if (minimumDistanceUm == null || maximumDistanceUm == null) {
    return '';
  }

  const minimumLabel = formatPeakDetectionDistanceUm(minimumDistanceUm);
  const maximumLabel = formatPeakDetectionDistanceUm(maximumDistanceUm);

  return minimumLabel === maximumLabel ? minimumLabel : `${minimumLabel}–${maximumLabel}`;
};

export const buildPeakDetectionSpacingDistanceGroups = ({
  minDistancePx,
  slideIds,
  umppBySlideId,
  customResolutionUmpp,
}: {
  minDistancePx?: number;
  slideIds?: string[];
  umppBySlideId?: Record<string, number>;
  customResolutionUmpp?: number | null;
}): PeakDetectionSpacingDistanceGroup[] => {
  if (!isFinite(minDistancePx) || Number(minDistancePx) < 1) {
    return [];
  }

  if (isFinite(customResolutionUmpp) && Number(customResolutionUmpp) > 0) {
    const distanceUm = convertPeakDetectionDistancePxToUm(minDistancePx, customResolutionUmpp);
    if (distanceUm == null) {
      return [];
    }

    return [
      {
        umpp: Number(customResolutionUmpp),
        distanceUm,
        slideCount: size(slideIds) || 1,
        isCustom: true,
      },
    ];
  }

  const slidesWithUmpp = compact(
    map(slideIds, (slideId) => {
      const umpp = umppBySlideId?.[slideId];
      return isFinite(umpp) && Number(umpp) > 0 ? { slideId, umpp: Number(umpp) } : null;
    })
  );
  const groupedSlides = groupBy(slidesWithUmpp, (slide) => String(slide.umpp));

  return compact(
    map(groupedSlides, (slides) => {
      const umpp = first(slides)?.umpp;
      const distanceUm = convertPeakDetectionDistancePxToUm(minDistancePx, umpp);
      if (distanceUm == null || !isFinite(umpp)) {
        return null;
      }

      return {
        umpp,
        distanceUm,
        slideCount: size(slides),
        isCustom: false,
      };
    })
  );
};
